import React, { useState } from "react";
import { useTranslation } from 'react-i18next';
import ConnectionProgressStep from "./ConnectionProgressStep";
import { HelpCenterIcon } from "../../common/ProjectIcons";
import { getHelpCenterUrl } from "../../../utils/getHelpCenterUrl";

type ConnectionProgressIndicatorProps = {
    stepOneStatus: 'completed' | 'in-progress' | 'upcoming';
    stepTwoStatus: 'completed' | 'in-progress' | 'upcoming';
    stepThreeStatus: 'completed' | 'in-progress' | 'upcoming';
    completed: boolean;
    completedSuccessfully?: boolean;
}

const ConnectionProgressIndicator = ({
    stepOneStatus,
    stepTwoStatus,
    stepThreeStatus,
    completed,
    completedSuccessfully = false,
}: ConnectionProgressIndicatorProps) => {
    const { t, i18n } = useTranslation();
    const [isVisible, setIsVisible] = useState(true);

    const helpCenterUrl = getHelpCenterUrl(i18n.language);

    const handleClose = () => {
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="flex flex-col items-start mt-4 mb-3 bg-light-gray dark:bg-neutral-gray-700 rounded-lg px-3 py-2 relative transition-colors duration-300">
            <div>
                <button
                    onClick={handleClose}
                    className="absolute top-2 right-4 text-gray-400 hover:text-gray-600 dark:text-gray-lines dark:hover:text-white focus:outline-none transition-colors duration-300"
                    aria-label="Close"
                >
                    <svg aria-hidden="true" className="w-5 h-5 text-off-black dark:text-white transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path></svg>
                </button>

                <label className="mt-3 text-2xl text-off-black dark:text-white transition-colors duration-300">{t("Connection Quick Start")}</label>
            </div>
            <div className="flex flex-col flex md:flex-row justify-between items-start md:items-center w-full mt-3 mb-2">
                {completedSuccessfully ?
                    <div>
                        <ConnectionProgressStep
                            stepLabel={completedSuccessfully ? t("Success! Your connection is set up. Monitor the logs for status updates.") : t("Your connection is created. In order to see data flow, add a destination and monitor the logs for status updates.")}
                            status={completedSuccessfully ? "completed" : "attention"}
                            finalStep={true} />
                    </div> :
                    <div className="flex flex-row bg-white dark:bg-off-black rounded-lg transition-colors duration-300">
                        <ConnectionProgressStep stepLabel={t("Connect Provider")} status={stepOneStatus} finalStep={false} />
                        <ConnectionProgressStep stepLabel={t("Connect Destination")} status={stepTwoStatus} finalStep={false} />
                        <ConnectionProgressStep stepLabel={t("Confirmation")} status={stepThreeStatus} finalStep={true} />
                    </div>}
                <div className="flex flex-row md:flex-col items-end mt-2 md:mt-0">
                    <a href={helpCenterUrl} target="_blank" rel="noopener noreferrer" className="items-center font-semibold text-dark-green dark:text-dark-green-100 hover:underline transition-colors duration-300"><HelpCenterIcon />{t("Help Center")}</a>
                    <label className="ml-3 text-off-black dark:text-white transition-colors duration-300">support@earthranger.com</label>
                </div>
            </div>
        </div>
    );
};

export default ConnectionProgressIndicator